import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

export default function BlogPostEditor({ postId, onClose, onSaved }) {
  const [post, setPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!postId) return;
    axios
      .get(`${API}/admin/blog/posts`)
      .then((res) => {
        const found = res.data.find((p) => p.id === postId);
        if (!found) {
          setError("Post not found");
        } else {
          setPost(found);
        }
      })
      .catch((e) => setError(e.response?.data?.detail || "Couldn't load post"));
  }, [postId]);

  const save = async (e) => {
    e?.preventDefault();
    if (!post) return;
    setSaving(true);
    try {
      const payload = {
        title: post.title,
        excerpt: post.excerpt,
        body_html: post.body_html,
        category: post.category,
        keywords: post.keywords,
      };
      await axios.patch(`${API}/admin/blog/posts/${postId}`, payload);
      toast.success("Saved");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="blog-editor-overlay"
    >
      <div
        className="bg-white border border-zinc-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold tracking-tight">Edit post</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="blog-editor-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        {error && (
          <p className="px-6 py-4 text-sm text-red-700">{error}</p>
        )}
        {!post && !error && (
          <p className="px-6 py-12 text-center text-xs uppercase tracking-[0.2em] text-zinc-400">Loading…</p>
        )}

        {post && (
          <form onSubmit={save} className="p-6 space-y-5">
            <Field label="Title">
              <input
                type="text"
                value={post.title}
                onChange={(e) => setPost({ ...post, title: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
                data-testid="blog-editor-title"
                required
              />
            </Field>

            <Field label="Slug (read-only)">
              <input
                type="text"
                value={post.slug}
                disabled
                className="w-full border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-500 font-mono"
              />
            </Field>

            <Field label="Excerpt">
              <textarea
                value={post.excerpt || ""}
                onChange={(e) => setPost({ ...post, excerpt: e.target.value })}
                rows={3}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900 resize-y"
                data-testid="blog-editor-excerpt"
              />
            </Field>

            <Field label="Category">
              <input
                type="text"
                value={post.category || ""}
                onChange={(e) => setPost({ ...post, category: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
                placeholder="e.g. productivity, ADHD"
              />
            </Field>

            <Field label="Keywords (comma separated)">
              <input
                type="text"
                value={(post.keywords || []).join(", ")}
                onChange={(e) =>
                  setPost({
                    ...post,
                    keywords: e.target.value.split(",").map((k) => k.trim()).filter(Boolean),
                  })
                }
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
              />
            </Field>

            <Field label="Body HTML">
              <textarea
                value={post.body_html || ""}
                onChange={(e) => setPost({ ...post, body_html: e.target.value })}
                rows={14}
                className="w-full border border-zinc-300 px-3 py-2 text-sm font-mono focus:outline-none focus:border-zinc-900 resize-y"
                data-testid="blog-editor-body"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Internal-linking rules are enforced server-side on save (max 5, first instance only, allowed paths only).
              </p>
            </Field>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-200 -mx-6 px-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-zinc-700 hover:text-zinc-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2 bg-zinc-900 hover:bg-[#002FA7] disabled:opacity-50 text-white text-xs font-medium uppercase tracking-[0.12em] transition-colors"
                data-testid="blog-editor-save"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
